<template>
    <base-modal :modal-size="'large'" :modal-title="'Edit Component'" @close="$emit('close')" @save="saveComponent()">
        <div class="split">
            <div class="half">
                <h3>Edit</h3>
                <form class="component-form">
                    <!-- General panel -->
                    <div class="panel panel-primary">
                        <div class="panel-header" role="tab">
                            <h3>General</h3>
                        </div>
                        <div class="panel-body">
                            <form class="form-inline">
                                <FormulateInput type="text" label="Title" v-model="componentModel.name" required /> 
                                <FormulateInput type="select" label="Visualization Type" v-model="componentModel.chart" :options="chartChoices" @input="fetchVizAxis" />
                                <h4>Filter Rule</h4>
                                <boolean-expression-widget :expression="componentModel.filter_rule" @updateExpression="updateFilterRule"></boolean-expression-widget>

                                <div v-if="xAxisName">
                                    <h4>{{ xAxisName }} <small v-if="!xAxisRequired">(optional)</small></h4>
                                    <div class="domain-item" v-for="domain in componentModel.domains">
                                        <FormulateInput type="text" v-model="domain.name" label="Label" />
                                        <FormulateInput type="select" :options="domainFieldOptions" label="Field" v-model="domain.field" />
                                        <FormulateInput type="button" label="Reset" @click="resetDomain()" />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <!-- Image panel -->
                    <div v-if="yAxisName" class="panel panel-primary series">
                        <div class="panel-header clearfix">
                            <h3 class="pull-left">{{yAxisName}}</h3>
                        </div>
                        <div class="panel-body">
                            <draggable :list="componentModel.images">
                                <transition-group name="list-complete">
                                    <div class="list-complete-item"
                                         v-for="(image, imageIndex) in componentModel.images" 
                                         v-bind:key="itemKey(image)">
                                        <form class="form-inline">
                                            <div class="form-group">
                                                <input type="hidden" v-model="image.order">
                                                <input type="text" id="image-name" placeholder="Image name" class="form-control" required
                                                       v-model.trim="image.name"
                                                       v-on:change="renderPreview()">
                                            </div>
                                            <div class="form-group">
                                                <select class="form-control" v-model="image.kind" v-on:change="renderPreview()">
                                                    <option value="null" disabled hidden>Select Kind</option>
                                                    <option v-for="choice in ImageKindChoices" 
                                                            :value="choice.id">{{choice.text}}</option>
                                                </select>
                                            </div>
                                            <button type="button" class="btn btn-link pull-right" @click="removeImage(imageIndex)">
                                                <i class="fa fa-close"></i>
                                            </button>
                                        </form>
                                        <br>
                                        <div class="table-responsive" style="display: initial;">
                                            <table class="table table-yaxis">
                                                <thead>
                                                    <tr>
                                                        <th>Serie name</th>
                                                        <th v-show="image.kind == 'score'">Scoring model</th>
                                                        <th v-show="image.kind == 'value'">Field</th>
                                                        <th v-show="image.kind != 'count'">Aggregations</th>
                                                        <th width="38px">
                                                            <button type="button" class="btn btn-default btn-xs pull-right" @click="addSerieToImage(imageIndex)">
                                                                Add serie
                                                            </button>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody v-for="(obj, index) in image.series" v-bind:key="itemKey(obj)">
                                                    <tr>
                                                        <tr>
                                                            <td>
                                                                <input type="hidden" v-model="obj.order">
                                                                <input type="text" class="form-control" placeholder="name" required
                                                                       v-model.trim="obj.name" v-on:change="renderPreview()">
                                                            </td>
                                                            <td v-show="image.kind == 'score'" v-bind:class="{ 'has-error': !obj.scoring_model && hasSumit && obj.aggregation !== undefined && obj.aggregation != 0 }">
                                                                <select class="form-control" 
                                                                        v-model="obj.scoring_model" 
                                                                        v-on:change="renderPreview()"
                                                                        v-bind:disabled="image.kind != 'score'">
                                                                    <option value="" disabled hidden>Select Scoring</option>
                                                                    <option v-for="choice in ScoringModelChoices" 
                                                                            :value="choice.id">{{choice.text}}</option>
                                                                </select>
                                                            </td>
                                                            <td v-show="image.kind == 'value'">
                                                                <FormulateInput type="select" :options="serieFieldOptions" v-model="obj.field" />
                                                            </td>
                                                            <td v-show="image.kind != 'count'">
                                                                <select class="form-control" 
                                                                        v-model="obj.aggregation"
                                                                        v-on:change="renderPreview()"
                                                                        v-bind:disabled="image.kind != 'score' && image.kind != 'value' && image.kind != 'freq'">
                                                                    <option value="null" disabled hidden>Select Aggregation</option>
                                                                    <option v-if="image.kind == 'score' || image.kind == 'value'" v-for="choice in AggregationChoices" :value="choice.id">{{choice.text}}</option>
                                                                    <option v-if="image.kind == 'freq'" v-for="choice in AggFreqChoices" :value="choice.id">{{choice.text}}</option>
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <button type="button" class="btn btn-link pull-right"
                                                                        @click="removeSerieFromImage(imageIndex, index)">
                                                                    <i class="fa fa-close"></i></button>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td colspan="5">
                                                                <boolean-expression-widget :expression="obj.filter_rule" @updateExpression="updateSerieFilterRule($event, obj)"></boolean-expression-widget>
                                                            </td>
                                                        </tr>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </transition-group>
                            </draggable>
                            <div class="button-wrapper">
                                <FormulateInput type="button" label="Add" @click="addImage" />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <div class="half">
                <h3>Preview</h3>
                <div class="preview-canvas" :class="{'metric-banner': componentModel.chart == 'MB'}">
                    <viz-widget
                        :renderData="renderData"
                        :height="5"
                        :chartId="previewTarget">
                    </viz-widget>
                </div>
            </div>
        </div>
    </base-modal>
</template>


<script>
 import Vue from 'vue';
 import draggable from 'vuedraggable'
 import Multiselect from 'vue-multiselect';
 import BooleanExpressionWidget from 'components/elements/BooleanExpressionWidget';
 import VizWidget from 'components/elements/VizWidget';
 import BasicModal from 'components/modals/BasicModal';
 import BaseModal from 'components/modals/BaseModal';

 // Load the full build. 
 var _ = require('lodash');

 export default {
   components: {
       draggable,
       BooleanExpressionWidget,
       Multiselect,
       VizWidget,
       BasicModal,
       BaseModal
   },
     
   props: {
     dashboardRules: null,
       datesRange: {
           type: Object,
           default () {
               return {
                   from: null,
                   to: null,
                   date_field: null
               }
           }
       },
       component: {
           type: Object,
           default () {
               return {
                   name: '',
                   chart: null,
                   domains: [],
                   images: []
               };
           }
       },
   },
     
     data () {
         return {
             loading: false,
             componentModel: null,
             editedSerie: null,
             xAxisName: null,
             xAxisRequired: false,
             yAxisName: null,
             isVisible: false,
             //ChartChoices: [],
             FieldReferencesOptions: [],
             fieldOptions: [],
             //ImageKindChoices: [], 
             ScoringModelChoices: [],
             //AggregationChoices: [],
             newDomain: {
                 name: null,
                 field: null
             },
             newImage: {
                 name: null,
                 kind: null,
                 series: []
             },
             newSerie: {
                 name: null,
                 aggregation: null,
                 scoring_model: null,
                 field: null
             },
             hasSumit: false,
             renderData: {}
         };
     },
     computed: {
         previewTarget () {
             return `preview-${Date.now()}`;
         },
         dashboardId () {
             return this.$route.params.id;
         },
         bodyHeight () {
             return $(window).height() - 300;
         },
         chartHeight () {
             return $(window).height() - 350;
         },
         ChartChoices () {
             return API.components.getChartChoices();
         },
         ImageKindChoices () {
             return [
                 { id: 'count', text: 'Count' },
                 { id: 'freq', text: 'Percentage' },
                 { id: 'value', text: 'Value' }
             ];
         },
         AggregationChoices () {
             return [
                 // { id: 'count', text: 'Count' },
                 { id: 'avg', text: 'Average' },
                 { id: 'max', text: 'Max' },
                 { id: 'min', text: 'Min' },
             ];
         },
         AggFreqChoices () {
             return [
                 { id: 'all', text: 'All' },
                 { id: 'bucket', text: 'Bucket' }
             ];
         },
         domainFieldOptions () {
             return this.fieldOptions
                        .filter(f => f.is_aggregable)
                        .map(f => {
                            return {
                                id: f['id'],
                                value: f['id'],
                                label: f['name'],
                            };
                        });
         },
         serieFieldOptions () {
             return this.fieldOptions
                        .filter(f => f.supports_agg_func)
                        .map(f => {
                            return {
                                id: f['id'],
                                value: f['id'],
                                label: f['name'],
                            };
                        });
         },
     },
     created () {
             // Init componentModel
         this.chartChoices = API.components.getChartChoices();
         this.initComponentModel(this.component);
         if (this.componentModel.chart != null) {
             this.fetchVizAxis(this.componentModel.chart, true);
         }
       // Fetching field list from server
         this.fetchFieldReferenceChoices();
         this.fetchScoringModelChoices();
         this.renderPreview();
     },
     mounted () {
     },
     methods: {
         //----------------------//
         // Fetching from server //
         //----------------------//
         itemKey(item) {
             if ('id' in item && item.id) {
                 return item.id;
             }
             if (!('_temp' in item)) {
                 item['_temp'] = Date.now().toString();
             }
             return "item-" + item._temp;
         },
         fetchVizAxis(kind, init) {
             init = init || false;
             var self = this;
             
             if (kind) {
                 API.viz.getVizAxis(kind)
                    .then(function (response) {
                        var results = response.data;
                        
                        if (!init) {
                            if (!results.x_axis_name || (results.x_axis_name != self.xAxisName)) {
                                self.componentModel.domains = [];
                                if (results.x_axis_name) {
                                    self.addDomain();
                                }
                            }
                            
                            if (results.y_axis_name != results.yAxisName) {
                                self.componentModel.images = [];
                                self.addImage();
                            }
                        }
                        
                        self.xAxisName = results.x_axis_name;
                        self.yAxisName = results.y_axis_name;
                        self.xAxisRequired = results.x_axis_required;
                        
                        if (init && self.xAxisName && !self.xAxisRequired && !self.componentModel.domains.length) {
                            self.componentModel.domains = [];
                            self.addDomain();
                        }
                    });
             }
         },
         updateFilterRule(expression) {
             this.$set(this.componentModel, 'filter_rule', expression);
             this.renderPreview();
         },
         updateSerieFilterRule(expression, serie) {
             this.$set(serie, 'filter_rule', expression);
             this.renderPreview();
         },
         fetchFieldReferenceChoices () {
             var self = this;
             API.fields.getList()
                .then(function (response) {
                    self.fieldOptions = response.data;
                });
         },
         
         fetchScoringModelChoices () {
             var self = this;
             API.scoringModels.getList()
                .then(function (response) {
                    var data = response.body;
                    var choices = []
                    for (var index in data) {
                        var choice = data[index];
                        
                        choices.push({"id": choice.id, "text": choice.name});
                    }
                    self.ScoringModelChoices = choices;
                    
                });
         },
         
         
         //------------//
         //   EXTRAS   //
         //------------//
         
         initComponentModel (component) {
             this.componentModel = _.cloneDeep(component);

         },
         resetDomain() {
             this.componentModel.domains = [];
             var domain = Vue.leadscorz.deep_clone(this.newDomain)
             this.componentModel.domains.push(domain);
             
         },
         
         addDomain () {
             var domain = Vue.leadscorz.deep_clone(this.newDomain)
             this.componentModel.domains.push(domain);
         },
         removeDomain (index) {
             this.componentModel.domains.splice(index, 1);
             this.renderPreview();
         },
         
         addImage () {
             // Cloning new image obj
             var image = Vue.leadscorz.deep_clone(this.newImage);
             // Cloning new serie obj
             var serie = Vue.leadscorz.deep_clone(this.newSerie);
             // Adding cloned serie to cloned image
             image.series.push(serie);
             // Adding new Image to ComponentModel
             this.componentModel.images.push(image);
         },
         removeImage (index) {
             this.componentModel.images.splice(index, 1);
             this.renderPreview();
         },
         
         addSerieToImage (index) {
             this.componentModel.images[index].series.push(Vue.leadscorz.deep_clone(this.newSerie));
         },
         removeSerieFromImage (imageIndex, index) {
             this.componentModel.images[imageIndex].series.splice(index, 1);
             this.renderPreview();
         },

         chartTypeOnChange() {
             this.fetchVizAxis();
             this.renderPreview()
         },
         
         
         //------------//
         // Validation //
         //------------//
         validateGeneral () {
             this.modelError.name = !(this.componentModel.name && this.componentModel.name.length);
             this.modelError.chart = !(this.componentModel.name && this.componentModel.name.length);
             
             var isInvalid = this.modelError.name || this.modelError.chart;
             
             return isInvalid;
         },
         validateDomains () {
             var isInvalid = false;
             
             for (var i in this.componentModel.domains) {
                 var domain = this.componentModel.domains[i];
                 
                 var domainError = {
                     name: !(domain.name && domain.name.length),
                     field: !domain.field
                 };
                 this.modelError.domains[i] = domainError;
                 
                 isInvalid = isInvalid || domainError.name || domainError.field;
             }
             
             return isInvalid;
         },
         validateSeries (image) {
             var isInvalid = false;
             var seriesErrors = [];
             
             for (var i in image.series) {
                 var serie = image.series[i];
                 seriesErrors.push({
                     name: !(serie.name && serie.name.length),
                     aggregation: !serie.aggregation
                 });
             }
             
             return [isInvalid, seriesErrors];
         },
         validateImages () {
             var isInvalid = false;
             
             for (var i in this.componentModel.images) {
                 var image = this.componentModel.images[i];
                 var seriesErrors = this.validateSeries(image);
                 
                 var imageError = {
                     name: !(image.name && image.name.length),
                     kind: !image.kind,
                     series: seriesErrors[1]
                 };
                 this.modelError.images[i] = imageError;
                 
                 isInvalid = isInvalid || imageError.name || imageError.kind || seriesErrors[0];
             }
             
             return isInvalid;
         },
         validate () {
             var isGeneralInvalid = this.validateGeneral();
             var areDomainsInvalid = this.validateDomains();
             var areImagesInvalid = this.validateImages();
             
             return isGeneralInvalid || areDomainsInvalid || areImagesInvalid;
         },
         
         
         //----------------//
         // Saving changes //
         //----------------//
         
         buildRequestData () {
             // ToDo: Check this!
             var self = this;
             var requestData = {
                 id: this.componentModel.id,
                 name: this.componentModel.name,
                 chart: this.componentModel.chart,
                 domains: [],
                 images: [],
                 filter_rule: this.componentModel.filter_rule
             };

             // Clean request data

             _.forEach(this.componentModel.domains, function (item) {
                 let d = _.cloneDeep(item);
                 if (!d.field && !self.xAxisRequired) {
                     return;
                 }
                 requestData.domains.push(d);
             })
             
             for (var i in this.componentModel.images) {
                 var currentImage = {
                     id: this.componentModel.images[i].id,
                     name: this.componentModel.images[i].name,
                     kind: this.componentModel.images[i].kind,
                     series: []
                 };
                 
                 for (var s in this.componentModel.images[i].series) {
                     var currentSerie = {
                         id: this.componentModel.images[i].series[s].id,
                         name: this.componentModel.images[i].series[s].name,
                         aggregation: this.componentModel.images[i].series[s].aggregation,
                         filter_rule: this.componentModel.images[i].series[s].filter_rule
                     };
                     
                     if (this.componentModel.images[i].kind == 'count') {
                         currentSerie.aggregation = 'count';         
                     }
                     if (this.componentModel.images[i].kind == 'score' && this.componentModel.images[i].series[s].scoring_model) {
                         currentSerie.scoring_model = this.componentModel.images[i].series[s].scoring_model;         
                     }
                     if (this.componentModel.images[i].kind == 'value' && this.componentModel.images[i].series[s].field) {
                         currentSerie.field = this.componentModel.images[i].series[s].field;         
                     }
                     // Checking the rules of the current image's serie   
                     
                     currentImage.series.push(currentSerie);              
                 }
                 requestData.images.push(currentImage);
             }
             
             return requestData;
         },
         
         saveComponent: _.debounce(function () {
             var self = this;
             var component = _.cloneDeep(self.componentModel);
             
             if (!self.xAxisRequired && !component.domains) {
                 component.domains = [];
             }
             self.$emit('close', component);
             
         }, 500),
         
         renderPreview: _.debounce(function () {
             var self = this;
             var requestData = this.buildRequestData();
             
             self.renderData = {};
             API.components.renderPreview(this.dashboardId, requestData, this.datesRange)
                .then(function (response) {
                    self.renderData = response.data;
                })
                .catch(() => console.log("Can't preview"));
         }, 500),
     }
 }
</script>


<style lang="scss">

 .split {
     flex-grow: 1;
     display: grid;
     grid-template: 40% 1fr / 1fr;
     grid-row-gap: 1rem;
     grid-column-gap: 1rem;
     min-width: 900px;
     height: 50vh;

     @media (min-aspect-ratio: 4/3) {
         grid-template: 1fr / 40% 1fr;
     }

     .half {
         padding: 10px;
         display: grid;
         grid-template-rows: auto 1fr;
         overflow: hidden;
         min-width: 0;
         min-height: 0;
         
         .component-form {
             display: flex;
             flex-wrap: nowrap;
             white-space: nowrap;
             overflow-x: auto;

             @media (min-aspect-ratio: 4/3) {
                 overflow-y: auto;
                 overflow-x: hidden;
                 flex-direction: column;
             }

             
             .panel {
                 display: flex;
                 flex-direction: column;
                 flex: 1;
                 flex-grow: 2;
                 margin-right: .5em;

                 @media (min-aspect-ratio: 4/3) {
                     margin-right: unset;
                 }
                 
                 &.series {
                     flex: 2;
                     flex-grow: 3;
                     min-width: 50rem;
                      @media (min-aspect-ratio: 4/3) {
                          min-width: unset;
                      }
                 }

                 &:first-child {
                     margin-left: auto;
                      @media (min-aspect-ratio: 4/3) {
                          margin-left: unset;
                          
                      }
                    
                 }
                 &:last-child {
                     margin-right: auto;
                     @media (min-aspect-ratio: 4/3) {
                          margin-right: unset;
                          margin-bottom: unset;
                      }
                 }

                 .panel-body {
                     flex: 1;
                     overflow-y: auto;

                     .button-wrapper {
                         margin-top: .5em;
                     }
                 }
             }
         }

         .preview-canvas {
             background: #ddd;
             border-radius: 4px;
             display: flex;
             align-items: center;
             justify-content: center;
             padding: 1em;

             &.metric-banner {
                 .viz-wrapper {
                     height: unset;
                     width: unset;
                 }
             }
         }
     }

}
</style>



// WEBPACK FOOTER //
// ComponentModal.vue?303f9698